import { useMemo } from "react";
import { Icon } from "@/components";
import { Button, Heading, LoadingProgress, Table, Text, Textarea } from "@/ui";
import { MUTATION_ADD_NOTES_TO_ACCOUNT } from "../../../apollo/module-operations/customer";
import { useForm, useNotification, useValidator } from "@/hooks";
import { useMutation } from "@apollo/react-hooks";
import styles from "./Notes.module.scss";

const Notes = ({ customers, loading, setCustomers, user }) => {
    const notify = useNotification();

    const { inputs, setInputs, inputChange } = useForm({ note: "" });

    const { validator, isValid, isSubmited, submited } = useValidator(
        [
            {
                field: "note",
                method: "isEmpty",
                validWhen: false,
                message: "This note is required.",
            },
        ],
        inputs,
    );

    const notes = customers?.notes ?? [];

    const [addNote, { loading: loadingAddNotes }] = useMutation(
        MUTATION_ADD_NOTES_TO_ACCOUNT,
        {
            onError: () => {
                notify("Error Adding New Note", "error");
            },
        },
    );

    const _loading = loading || false;

    const rows = useMemo(
        () =>
            notes.map((note, index) => ({
                key: String(index),
                notes: note,
            })),
        [notes],
    );

    const columns = useMemo(
        () => [
            {
                id: "notes",
                header: "Notes",
                width: "100%",
                minWidth: 240,
                renderCell: row => (
                    <div className={styles.primaryCell}>{row.notes}</div>
                ),
            },
        ],
        [],
    );

    const addNoteCustomer = async () => {
        isSubmited(true);

        if (!isValid()) return;

        const noteToAdd = inputs.note;
        const nextNotes = [...notes, noteToAdd];

        try {
            const result = await addNote({
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                    input: {
                        id: customers.entity_id,
                        notes: nextNotes,
                    },
                },
            });

            const fromServer = result?.data?.addNotesToAccount?.notes;
            setCustomers?.(prev => ({
                ...prev,
                notes: Array.isArray(fromServer) ? fromServer : nextNotes,
            }));
            setInputs({ note: "" });
            isSubmited(false);
            notify("Added New Note Successfully", "success");
        } catch {
            // onError already notifies
        }
    };

    const noteError =
        submited && validator.note.isInvalid
            ? validator.note.message
            : undefined;

    return (
        <div className={`tap__panel_notes ${styles.root}`}>
            <div className={styles.header}>
                <Heading level={5}>Notes</Heading>
            </div>
            {_loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <Table
                    columns={columns}
                    emptyMessage="No notes"
                    getRowId={row => row.key}
                    rows={rows}
                />
            )}
            <div className={styles.form}>
                <Heading className={styles.formTitle} level={6}>
                    Add Notes
                </Heading>
                <Text
                    className={styles.formHint}
                    tone="secondary"
                    variant="body-sm"
                >
                    Enter any notes that you consider important in the process
                </Text>
                <Textarea
                    error={noteError}
                    fullWidth
                    label="Write the note..."
                    name="note"
                    onChange={inputChange}
                    rows={10}
                    value={inputs?.note || ""}
                />
                <div className={styles.actions}>
                    <Button
                        disabled={loadingAddNotes}
                        iconLeft={
                            !loadingAddNotes ? <Icon icon="plus" /> : undefined
                        }
                        loading={loadingAddNotes}
                        onClick={addNoteCustomer}
                        type="button"
                        variant="primary"
                    >
                        Add New Note
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Notes;
